import React, { FC, useEffect, useState, useMemo } from 'react';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import ReactDOMServer from 'react-dom/server';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import { Anchor, AnchorMode } from '../Anchor';
import { useData } from '@hooks/useData';
import { useGetAllDrivers } from '../../../services/useGetAllDrivers';
import { useNotifications } from '@hooks/useNotifications';
import { MapPin, MapPinVariants } from '../MapPin';
import { DriverDetail } from '../../molecules/DriverDetail/DriverDetail';
import { calculateDistance } from './../../../utils/distance';
import { SlideUpPanel } from '@components/atoms/SlideUpPanel/SlideUpPanel';
import { useGetUser } from '@services/useGetUser';
import { useUserLocation } from '@hooks/useUserLocation';
import { useJitteredLocations } from '@hooks/useJitteredLocations';
import { useZoomToUser } from '@hooks/useZoomToUser';
import { useTranslation } from 'react-i18next';
import iconRetinaUrl from 'leaflet/dist/images/marker-icon-2x.png';
import iconUrl from 'leaflet/dist/images/marker-icon.png';
import shadowUrl from 'leaflet/dist/images/marker-shadow.png';
import { Map as LeafletMap } from 'leaflet';
import { useRef } from 'react';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { Driver, locationProps, LastMapLocation } from '@types';

if (L.Icon.Default) {
    L.Icon.Default.mergeOptions({
        iconRetinaUrl,
        iconUrl,
        shadowUrl,
    });
}

const MapRefSetter: FC<{ mapRef: React.MutableRefObject<LeafletMap | null> }> = ({ mapRef }) => {
    const map = useMap();

    useEffect(() => {
        mapRef.current = map;
    }, [map, mapRef]);

    return null;
};

const ZoomComponent = ({ userLocation }: { userLocation: { lat: number; lng: number } | null }) => {
    useZoomToUser(userLocation);
    return null;
};

export const Map: FC<locationProps> = ({ locations, style }) => {
    const { t } = useTranslation();
    const jitteredLocations = useJitteredLocations(locations);
    const defaultCenter: [number, number] =
        jitteredLocations.length > 0 ? [jitteredLocations[0].latitude, jitteredLocations[0].longitude] : [38.736946, -9.142685];

    const defaultStyle = { height: '165px', width: '100%' };
    const containerStyle = style ? JSON.parse(style) : defaultStyle;

    const [selectedDriver, setSelectedDriver] = useState<Driver | null>(null);
    const { login, lastLocationSaved, allSettings, availableDrivers, user, lastMapLocation, setLastMapLocation } = useData();
    const { request: getAllDrivers, lastResponseTime, error } = useGetAllDrivers();
    const { request: requestUser } = useGetUser();
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { lastLocation } = useUserLocation(allSettings);
    const { showDefaultNotification } = useNotifications();
    const [distanceToDriver, setDistanceToDriver] = useState<string | null>(null);
    const mapRef = useRef<LeafletMap | null>(null);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [showDetail, setShowDetail] = useState(false);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [isExiting, setIsExiting] = useState(false);

    const handleSelectDriver = (driver: Driver) => {
        setSelectedDriver(driver);
        setIsExiting(false);
        setShowDetail(false);
        requestAnimationFrame(() => setShowDetail(true));
    };

    useEffect(() => {
        if (!login) return;

        if (!availableDrivers) {
            getAllDrivers();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [login, availableDrivers]);

    useEffect(() => {
        if (lastResponseTime) {
            if (error) {
                showDefaultNotification({ type: 'error', message: error });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastResponseTime]);

    useEffect(() => {
        return () => {
            if (mapRef.current) {
                const map = mapRef.current;
                const lastLocation = {
                    center: [map.getCenter().lat, map.getCenter().lng] as [number, number],
                    zoom: map.getZoom(),
                    lastUpdated: new Date().toISOString(),
                };
                setLastMapLocation(lastLocation);
                localStorage.setItem('lastMapLocation', JSON.stringify(lastLocation));
            }
        };
    }, [setLastMapLocation]);

    useEffect(() => {
        if (!login) return;
        requestUser();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [login]); // Fetch on mount

    useEffect(() => {
        const units: 'km' | 'miles' = user?.data?.units === 'miles' ? 'miles' : 'km';

        if (lastLocationSaved && selectedDriver?.latitude != null && selectedDriver?.longitude != null) {
            const distance = calculateDistance(
                lastLocationSaved.coordinates[0],
                lastLocationSaved.coordinates[1],
                selectedDriver.latitude,
                selectedDriver.longitude,
                units
            );
            setDistanceToDriver(distance);
        } else {
            setDistanceToDriver(null);
        }
    }, [lastLocationSaved, selectedDriver, user?.data?.units]);

    const initialCenter: [number, number] = (() => {
        if (lastMapLocation) {
            return lastMapLocation.center;
        } else if (lastLocationSaved) {
            return lastLocationSaved.coordinates;
        }
        return defaultCenter;
    })();
    const initialZoom = lastMapLocation?.zoom ?? 13;

    const zoomToCoordinates = lastMapLocation
        ? null
        : lastLocationSaved
          ? { lat: lastLocationSaved.coordinates[0], lng: lastLocationSaved.coordinates[1] }
          : null;

    const currentUserPin = useMemo(
        () =>
            L.divIcon({
                html: ReactDOMServer.renderToString(<MapPin variant={MapPinVariants.variant1} />),
                className: '',
                iconSize: [40, 40],
                iconAnchor: [20, 40],
            }),
        []
    );

    const driverPins = useMemo(() => {
        return (availableDrivers?.data ?? []).reduce((acc: Record<string, L.DivIcon>, driver: Driver) => {
            if (!driver.latitude || !driver.longitude) return acc;
            acc[driver.uuid] = L.divIcon({
                html: ReactDOMServer.renderToString(<MapPin variant={MapPinVariants.variant3} pillName={driver.name} />),
                className: '',
                iconSize: [40, 40],
                iconAnchor: [20, 40],
            });
            return acc;
        }, {});
    }, [availableDrivers?.data]);

    return (
        <>
            <MapContainer center={initialCenter} zoom={initialZoom} style={{ ...containerStyle, zIndex: 1 }} zoomControl={false}>
                <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                />
                {jitteredLocations.map((location, index) => (
                    <Marker key={index} position={[location.latitude, location.longitude]}>
                        <Popup>
                            <Anchor href={''} mode={AnchorMode.link}>
                                <strong>{location.name}</strong>
                            </Anchor>
                            <br />
                            {t('map.latitude')}: {Number(location.latitude).toFixed(6)}
                            <br />
                            {t('map.latitude')}: {Number(location.longitude).toFixed(6)}
                        </Popup>
                    </Marker>
                ))}

                <MapRefSetter mapRef={mapRef} />

                {zoomToCoordinates && <ZoomComponent userLocation={zoomToCoordinates} />}

                {/* Current User Location Marker */}
                {lastLocationSaved && (
                    <Marker
                        position={[lastLocationSaved.coordinates[0], lastLocationSaved.coordinates[1]]}
                        icon={currentUserPin}
                    />
                )}

                {availableDrivers?.data?.map((driver: Driver) => {
                    if (!driver.latitude || !driver.longitude) return null;
                    return (
                        <Marker
                            key={driver.uuid}
                            position={[Number(driver.latitude), Number(driver.longitude)]}
                            icon={driverPins[driver.uuid]}
                            eventHandlers={{ click: () => handleSelectDriver(driver) }}
                        />
                    );
                })}
            </MapContainer>

            {selectedDriver && (
                <SlideUpPanel isOpen={!!selectedDriver} onClose={() => setSelectedDriver(null)} maxWidth="600px">
                    <DriverDetail
                        to_uuid={selectedDriver?.uuid ?? ''}
                        src={selectedDriver?.profile_photo_url || undefined}
                        labelText={selectedDriver?.name}
                        distance={lastLocationSaved?.locationAccessGranted && distanceToDriver ? distanceToDriver : undefined}
                        phone_number={selectedDriver?.phone_number || ''}
                        car={`${selectedDriver?.vehicle_brand || ''} / ${selectedDriver?.vehicle_model || ''}`.trim()}
                        vehicle_seat_number={selectedDriver?.vehicle_seat_number || 0}
                        status={selectedDriver?.share_location || false}
                        onClose={() => setSelectedDriver(null)}
                    />
                </SlideUpPanel>
            )}
        </>
    );
};

export default Map;
